import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/Api";
import { useCommon } from "../context/CommonContext";
import Loading from "./Loading";
import { useQuery } from "@tanstack/react-query";
import { useLanguage } from "../context/LanguageProvider";

// ===== Custom Hook: Fetch Implicit Questions =====
// This encapsulates the @tanstack/react-query call for implicit association test questions
// • Uses a unique queryKey so results are cached per surveyId
// • queryFn calls api.fetchImplicitQuestions to retrieve questions from your backend
function useImplicitQuestions(surveyId){
    return useQuery({
        queryKey: ["implicit", surveyId],
        queryFn: ()=> api.fetchImplicitQuestions(surveyId)
    })
}

const IATDef = () => {
    const navigate = useNavigate(); // For navigating between screens after instructions

    const { translation } = useLanguage(); // Localisation function

    // ===== CONSTANTS =====
    const continueTimer = 10000; // 10 sec delay before user can proceed between steps

    // ===== STATE =====
    const [canContinue, setCanContinue] = useState(false); // Controls if next step is enabled
    const [attributedescriptions, setAttributedescriptions] = useState([[]]); // Each "screen" = array of "attribute-description" strings
    const [currentIndex, setCurrentIndex] = useState(0); // Which set of descriptions we are currently showing

    // Global shared state/actions from context
    const { surveyId, setAttributes, setPrompts, setQuestionID } = useCommon();

    // Load implicit questions from backend via custom hook
    const { data, status, error, fetchStatus } = useImplicitQuestions(surveyId);

    // ===== Effect: Process Loaded Data =====
    useEffect(()=>{
        if(status === "success"){ // Only process data after successful fetch
            if (data && data.length > 0) {
                // Temp arrays to gather all question attributes & descriptions & prompts
                let allAttributes = [];
                let allDescriptions = [];
                let allPrompts = [];
                let allQuestionIDS = [];

                // Aggregate separate arrays across each question block
                data.forEach(item =>{
                    allAttributes.push(item.attributes);
                    allDescriptions.push(item.attributedescriptions);
                    allPrompts.push(item.prompt);
                    allQuestionIDS.push(item.question_id);
                });

                // Push question data into global context
                setPrompts(allPrompts);
                setAttributes(allAttributes);
                setQuestionID(allQuestionIDS);

                // Combine each attribute with its corresponding description into one string
                // e.g. ("Happy", "positive feeling") → "Happy-positive feeling"
                const combineDescriptions = allDescriptions.map((descriptions, index)=>{
                    return descriptions.map((description, idx) =>{
                        return `${allAttributes[index][idx]}-${description}`;
                    })
                });

                // Store in local state for rendering
                setAttributedescriptions(combineDescriptions);
            } else {
                // ===== DEFAULT FALLBACKS — when API returns no data
                setAttributes([["Default-1", "Default-2", "Default-3", "Default-4", "Default-5", "Default-6"]]);
                setAttributedescriptions([[
                    "Default-1-Default description",
                    "Default-2-Default description",
                    "Default-3-Default description",
                    "Default-4-Default description",
                    "Default-5-Default description",
                    "Default-6-Default description"
                ]]);
                setPrompts(["Default prompt"]);
                setQuestionID([100]);
            }
        } 
        else if (status === "error" && error.message === "Error: 404") {
            // ===== DEFAULT FALLBACKS — when API returns a handled 404
            setAttributes([["Default-1", "Default-2", "Default-3", "Default-4", "Default-5", "Default-6"]]);
            setAttributedescriptions([[
                "Default-1-Default description",
                "Default-2-Default description",
                "Default-3-Default description",
                "Default-4-Default description",
                "Default-5-Default description",
                "Default-6-Default description"
            ]]);
            setPrompts(["Default prompt"]);
            setQuestionID([100]);
        }
    // Dependencies: we react when status changes or initialised data updates
    },[status, data, error, setAttributes, setAttributedescriptions]);

    // ===== Effect: Start Continue Timer (per "screen") =====
    useEffect(() =>{
        setCanContinue(false); // Initially disable Spacebar navigation
        const timer = setTimeout(()=>{
            setCanContinue(true); // Allow navigation after delay
        }, continueTimer);

        return () => clearTimeout(timer); // Cleanup timer
    },[currentIndex]); // Reset timer when user moves to new index

    // ===== Keyboard Handler =====
    const handleKeyPress = (event) => {
        if (canContinue && event.code === "Space") {
            console.log(`${currentIndex} - ${attributedescriptions.length}`);
            if (currentIndex >= attributedescriptions.length-1) {
                // If we’re at the final set — go to next phase "/IATDir"
                navigate("/IATDir");
            } else {
                // Otherwise, advance to next group of attribute-descriptions
                setCurrentIndex((prevIndex) => prevIndex + 1);
            }
        }          
    };

    // ===== Effect: Global Key Binding for Spacebar =====
    useEffect(()=>{
        window.addEventListener("keydown", handleKeyPress);
        return () => window.removeEventListener("keydown", handleKeyPress);
    },[canContinue, currentIndex]); // Rebind if continue state or index changes

    // ===== UI: Loading State =====
    if (fetchStatus  === "fetching" || status === "pending") {
        return <Loading />; // Show spinner/loading indicator until data is ready
    }   

    // ===== UI: Error State (excluding handled 404 which uses defaults) =====
    if (status === "error" && error.message !== "Error: 404") {
        if (error.message.startsWith("Error:")) {
            return <div>{translation("Error")} {error.message}</div>;
        }
        return <div>{translation("Network_error")}</div>;
    }

    // ===== UI: Main View =====
    return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col items-center justify-center relative">

                {/* Intro heading — only on very first screen */}
                { currentIndex < 1 &&
                <h3 className="text-4xl sm:text-5xl lg:text-6xl text-gray-800 mb-4 text-left w-full max-w-6xl">
                    {translation("IATDef_associationTestStart")}
                </h3>
                }
                
                {/* Context message: first screen vs subsequent screens have slightly different text */}
                {
                    currentIndex < 1 
                    ? (
                        <h6 className="text-2xl sm:text-3xl text-gray-800 mb-4 text-left w-full max-w-6xl">
                            {translation("IATDef_wordsAndMeanings")}
                        </h6>
                    ) 
                    : (
                        <h6 className="text-2xl sm:text-3xl text-gray-800 mb-4 text-left w-full max-w-6xl">
                            {translation("IATDef_wordsAndMeaningsOthers")}
                        </h6>
                    )
                }

                {/* Scrollable list of current attribute–description items */}
                <div className="mb-4 w-full max-w-6xl text-left text-lg sm:text-xl lg:text-2xl">
                    <ul className="list-disc pl-6 ml-6">
                        {attributedescriptions[currentIndex].map((item, index) => (
                            <li key={index} className="break-words">{item}</li>
                        ))}
                    </ul>
                </div>

                {/* Instructions for familiarisation */}
                <h6 className="text-2xl sm:text-3xl text-gray-800 mb-3 text-left w-full max-w-6xl">
                    {translation("IATDef_familiarizeInstructions")}
                </h6>

                {/* Helper prompt appears only when canContinue = true */}
                {canContinue && (
                    <p className="absolute -bottom-10 text-2xl sm:text-3xl text-gray-800 mt-4">
                        {translation("IATDef_pressSpaceToContinue")}
                    </p>
                )}
            </div>   
        </div>
    );
};

export default IATDef;